import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";

const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const AI_SERVICES = [
  {
    badge: "Ship AI-Powered Products",
    title: "LLM Integration & Development",
    desc: "We build production-grade LLM-powered features using GPT-4o, Claude 3.5, Gemini, and open-source models like Llama and Mistral. From RAG (Retrieval-Augmented Generation) pipelines to custom fine-tuning, our AI development company delivers models that actually work in production — not just demos. We handle context management, prompt engineering, token optimization, and safety guardrails.",
    features: ["GPT-4o & Claude API integration", "RAG pipeline architecture", "Custom fine-tuning workflows", "Prompt engineering & optimization", "Hallucination mitigation strategies"],
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
    badgeColor: "border-violet-500/30 bg-violet-500/10 text-violet-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456z" />
      </svg>
    ),
  },
  {
    badge: "End-to-End Automation",
    title: "AI Workflow Automation",
    desc: "Our AI automation services eliminate the repetitive, manual work slowing down your team. We build intelligent automation pipelines that connect your existing tools — CRM, ERP, helpdesk, billing — with AI decision-making layers. Whether it's auto-classifying support tickets, generating first-draft contracts, or routing leads based on intent signals, our custom AI solutions create measurable operational leverage for your business.",
    features: ["Intelligent document processing", "Multi-step agentic workflows", "CRM & ERP AI integration", "Email & calendar automation", "Custom trigger & action pipelines"],
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "text-amber-400",
    badgeColor: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
      </svg>
    ),
  },
  {
    badge: "24/7 Intelligent Support",
    title: "Custom AI Chatbot Development",
    desc: "We build AI chatbots and virtual assistants that go far beyond canned responses. Our custom AI solutions are trained on your product documentation, support history, and business logic — delivering accurate, on-brand answers that convert visitors and deflect tickets. Built on GPT-4o with seamless human handover, our chatbots integrate with your website, mobile app, WhatsApp, and BerryChat CRM out of the box.",
    features: ["GPT-4o powered conversations", "Knowledge base training", "Multi-channel deployment (web, app, WhatsApp)", "Live agent handover protocol", "Analytics & conversation insights"],
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
    badgeColor: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
  },
  {
    badge: "Data-Driven Intelligence",
    title: "Machine Learning Development",
    desc: "From predictive analytics to recommendation engines, our machine learning development services turn your data into a competitive moat. We design, train, and deploy custom ML models using PyTorch, TensorFlow, and scikit-learn — with full MLOps pipelines on AWS SageMaker, GCP Vertex AI, or Azure ML. Our machine learning solutions are engineered for production: monitored, versioned, and retrained automatically.",
    features: ["Predictive analytics models", "Recommendation & personalization engines", "Anomaly detection systems", "Computer vision applications", "Full MLOps pipeline & deployment"],
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "text-emerald-400",
    badgeColor: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    badge: "Expert Strategic Guidance",
    title: "AI Consulting Services",
    desc: "Not sure where AI fits in your business? Our AI consulting services help US and Canadian companies identify the highest-ROI AI opportunities, evaluate build-vs-buy decisions, and create a practical implementation roadmap. We've helped startups launch their first AI feature and Fortune 500 teams retrain legacy systems. Our AI consultants bring hands-on engineering experience — not just frameworks and slide decks.",
    features: ["AI readiness assessment", "Use-case prioritization workshop", "Vendor & model evaluation", "Risk & compliance review", "Technical architecture planning"],
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
    badgeColor: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
      </svg>
    ),
  },
  {
    badge: "Voice & Multimodal",
    title: "AI Agent Development",
    desc: "We build autonomous AI agents that plan, reason, and execute multi-step tasks without constant human supervision. Using frameworks like LangGraph, CrewAI, and AutoGen, our AI development company creates agents that browse the web, query databases, write code, and coordinate with other agents to complete complex workflows. From customer-facing agents to internal copilots, we ship agents that actually deliver measurable outcomes.",
    features: ["LangGraph & LangChain agents", "Multi-agent orchestration", "Tool use & function calling", "Memory & context persistence", "Human-in-the-loop checkpoints"],
    color: "border-fuchsia-500/30 bg-fuchsia-500/5",
    accent: "text-fuchsia-400",
    badgeColor: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7">
        <path d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25zm.75-12h9v9h-9v-9z" />
      </svg>
    ),
  },
];

const TECH_STACK = [
  "GPT-4o", "Claude 3.5", "Gemini 1.5", "Llama 3.1", "Mistral", "LangChain",
  "LangGraph", "CrewAI", "Pinecone", "Weaviate", "pgvector", "Python",
  "FastAPI", "PyTorch", "TensorFlow", "AWS SageMaker", "GCP Vertex AI", "Hugging Face",
];

const STATS = [
  { value: "50+", label: "AI features shipped to production" },
  { value: "10M+", label: "AI API calls processed monthly" },
  { value: "40%", label: "Avg. cost reduction via automation" },
  { value: "6 wks", label: "Avg. time from brief to live AI feature" },
];

export default function AIServicesPage() {
  useSEO({
    title: "AI Development Services — Custom AI Solutions | BerryByte",
    description: "Leading AI development company offering custom AI solutions, LLM integration, AI automation, chatbot development & ML services for US & Canadian businesses. Get a free AI consultation.",
    keywords: "AI development company, custom AI solutions, AI integration services, LLM development, AI automation agency, GPT-4 integration, AI consulting services, machine learning development, enterprise AI solutions",
    canonical: "https://www.berrybyteinn.com/ai-services",
  });

  return (
    <SharedLayout>
      <div className="relative bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] bg-violet-600 -top-48 -right-48" />
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-600 top-1/3 -left-40" />
        <GlowOrb className="w-[400px] h-[400px] bg-fuchsia-600 bottom-1/4 right-0" />

        {/* ── Hero ── */}
        <section className="relative pt-36 pb-20 px-6">
          <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />
          <div className="relative max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6">
              AI Development Company — US &amp; Canada
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Custom AI Solutions That<br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent"> Deliver Real Business Results</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-4 leading-relaxed">
              BerryByte is a leading <strong className="text-slate-300">AI development company</strong> helping startups and enterprises
              across the US and Canada build <strong className="text-slate-300">custom AI solutions</strong> — from LLM-powered applications
              and intelligent automation to machine learning systems that operate at scale.
            </p>
            <p className="text-lg text-slate-500 max-w-xl mx-auto mb-10">
              We don't sell AI strategy decks. We ship production-grade AI features with measurable ROI.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-cyan-600 text-white font-semibold hover:opacity-90 transition-opacity">
                Get a Free AI Consultation
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                  <path d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <Link to="/crm" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/10 bg-white/[0.03] text-slate-300 font-semibold hover:bg-white/[0.06] transition-colors">
                See BerryChat AI in Action
              </Link>
            </div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="relative py-12 border-y border-white/5">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {STATS.map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl font-black bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Services Grid ── */}
        <section className="relative py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                Our AI Development Services
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                End-to-end <strong className="text-slate-300">AI integration services</strong> — from a single LLM feature to a fully autonomous multi-agent system.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {AI_SERVICES.map((svc) => {
                const Icon = svc.icon;
                return (
                  <div key={svc.title} className={`rounded-2xl border p-7 ${svc.color} flex flex-col`}>
                    <div className={`p-3 rounded-xl border ${svc.color} ${svc.accent} w-fit mb-4`}>
                      <Icon />
                    </div>
                    <div className={`inline-flex px-3 py-1 rounded-full text-xs font-medium border mb-3 w-fit ${svc.badgeColor}`}>
                      {svc.badge}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">{svc.title}</h3>
                    <p className="text-slate-400 leading-relaxed text-sm mb-5 flex-1">{svc.desc}</p>
                    <ul className="space-y-2">
                      {svc.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-xs text-slate-400">
                          <CheckIcon />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Why BerryByte AI ── */}
        <section className="relative py-20 px-6 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300 text-sm font-medium mb-6">
                  Why BerryByte AI
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-6 leading-tight">
                  An AI Development Company That Builds, Not Just Advises
                </h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Most AI consulting firms hand you a roadmap and leave. BerryByte engineers ship. Our
                  <strong className="text-slate-300"> AI integration services</strong> team includes senior LLM engineers, ML scientists,
                  and full-stack developers who have built AI features used by millions of users across the US, Canada, and beyond.
                </p>
                <p className="text-slate-400 leading-relaxed mb-8">
                  Whether you need to add a <strong className="text-slate-300">GPT-4 integration</strong> to your SaaS product,
                  automate a complex internal workflow, or build a domain-specific AI agent from scratch, we provide
                  the hands-on engineering muscle to get it done — on time and on budget.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold hover:opacity-90 transition-opacity text-sm">
                  Talk to an AI Engineer
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
              <div className="space-y-4">
                {[
                  { title: "Production-First Engineering", desc: "We optimize for latency, cost, and reliability — not just accuracy in notebooks. Every AI feature ships with monitoring and fallback handling." },
                  { title: "Model-Agnostic Expertise", desc: "We work with OpenAI, Anthropic, Google, and open-source models. You get the best model for your use case, not just the one we prefer." },
                  { title: "Security & Privacy by Default", desc: "No customer data sent to third-party models without consent. We implement private deployments, data masking, and GDPR/CCPA-compliant AI pipelines." },
                  { title: "Continuous Improvement", desc: "We set up evaluation pipelines so your AI gets smarter over time — not just once at launch." },
                ].map((item) => (
                  <div key={item.title} className="p-5 rounded-xl border border-white/8 bg-white/[0.02]">
                    <h3 className="text-white font-semibold text-sm mb-1">{item.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Tech Stack ── */}
        <section className="relative py-20 px-6 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">AI Technologies We Work With</h2>
              <p className="text-slate-400 max-w-lg mx-auto text-sm">
                We stay at the frontier — integrating the latest models, frameworks, and infrastructure tools your product needs.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              {TECH_STACK.map((tech) => (
                <span key={tech} className="px-4 py-2 rounded-xl border border-white/10 bg-white/[0.03] text-slate-400 text-sm font-medium hover:border-violet-500/40 hover:text-white transition-colors">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── Industries ── */}
        <section className="relative py-20 px-6 border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-3">Industries We Serve</h2>
              <p className="text-slate-400 max-w-lg mx-auto text-sm">
                Our AI solutions span every industry vertical — built to meet the specific compliance, data, and scale requirements of each sector.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: "SaaS & Software", desc: "AI copilots, in-app assistants, and predictive features that increase activation and retention." },
                { name: "Healthcare & MedTech", desc: "HIPAA-compliant AI for clinical documentation, patient triage, and medical coding automation." },
                { name: "Fintech & Banking", desc: "Fraud detection models, document AI for KYC/AML, and intelligent financial advisors." },
                { name: "E-Commerce & Retail", desc: "Product recommendation engines, AI-powered search, and demand forecasting models." },
                { name: "Legal & Compliance", desc: "Contract analysis, legal document drafting, and regulatory compliance automation." },
                { name: "Logistics & Operations", desc: "Route optimization, predictive maintenance, and supply chain intelligence platforms." },
              ].map((ind) => (
                <div key={ind.name} className="p-5 rounded-xl border border-white/8 bg-white/[0.02]">
                  <h3 className="text-white font-semibold text-sm mb-2">{ind.name}</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">{ind.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="relative py-24 px-6 border-t border-white/5">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6">
              Free 30-Minute AI Consultation
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Build Your AI-Powered Future Today
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
              Tell us your biggest manual bottleneck or the AI feature your product is missing.
              We'll scope it, price it, and give you a realistic timeline — no pressure.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-10 py-4 rounded-xl bg-gradient-to-r from-violet-600 via-fuchsia-600 to-cyan-600 text-white font-bold text-lg hover:opacity-90 transition-opacity"
            >
              Start My AI Project
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
        </section>
      </div>
    </SharedLayout>
  );
}
